import { Brain, Check, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { ConsultationStepper } from '@/components/ConsultationStepper';
import { SymptomBlockPager } from '@/components/SymptomBlockPager';
import { reorderQuestionsByWeight, reorderUnvisitedQuestions } from '@/utils/symptomReordering';
import { logTemporaryRemedies } from '@/utils/logTemporaryRemedies';
import { getOrCreateE2EEKey } from '@/utils/e2ee';

interface MindQuestionItem {
  symptom: string;
  question: string;
}

export function Mind() {
  const { patient, setToast, setErrorModal } = useGlobalContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<MindQuestionItem[]>([]);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [step, setStep] = useState<'init' | 'questions' | 'done'>('init');

  // Pagination and target validation states
  const [blockSize, setBlockSize] = useState<number>(5);
  const [targetCount, setTargetCount] = useState<number>(5);
  const [autoAdvance, setAutoAdvance] = useState<boolean>(true);
  const [currentBlock, setCurrentBlock] = useState<number>(0);
  const [validatedSymptoms, setValidatedSymptoms] = useState<string[]>([]);
  const [symptomRemedyWeights, setSymptomRemedyWeights] = useState<Record<string, Record<string, number>>>({});
  const [symptomRemedyCounts, setSymptomRemedyCounts] = useState<Record<string, number>>({});
  const [symptomRemedyHighRemediesCounts, setSymptomRemedyHighRemediesCounts] = useState<Record<string, { principal_high_remedies: number; total_high_remedies: number; ratio_high_remedies: number }>>({});
  const [linkedByRemedySymptoms, setLinkedByRemedySymptoms] = useState<string[]>([]);
  const [visitedBlocks, setVisitedBlocks] = useState<Set<number>>(new Set([0]));

  const handleBlockChange = useCallback((newBlock: number) => {
    if (!visitedBlocks.has(newBlock)) {
      const currentSelected = Object.entries(answers)
        .filter(([_, val]) => val)
        .map(([key]) => key);
      setQuestions((prevQuestions) =>
        reorderUnvisitedQuestions(
          prevQuestions,
          blockSize,
          visitedBlocks,
          (q) => [q.symptom],
          validatedSymptoms,
          currentSelected,
          symptomRemedyWeights,
          symptomRemedyCounts,
          linkedByRemedySymptoms,
          { symptomRemedyHighRemediesCounts }
        )
      );
      setVisitedBlocks((prev) => new Set([...prev, newBlock]));
    }
    setCurrentBlock(newBlock);
  }, [answers, blockSize, symptomRemedyCounts, symptomRemedyWeights, linkedByRemedySymptoms, validatedSymptoms, visitedBlocks, symptomRemedyHighRemediesCounts]);

  useEffect(() => {
    getOrCreateE2EEKey().catch(() => {});
  }, []);

  useEffect(() => {
    apiClient
      .get('/symptoms/validation-config')
      .then((res) => {
        if (res.data?.block_size) setBlockSize(res.data.block_size);
        if (res.data?.target_affirmative_step4) setTargetCount(res.data.target_affirmative_step4);
        if (typeof res.data?.auto_advance_on_target === 'boolean') {
          setAutoAdvance(res.data.auto_advance_on_target);
        }
      })
      .catch(() => {});
  }, []);

  const startAnalysis = useCallback(async () => {
    if (!patient.email?.trim()) {
      setToast({ type: 'error', message: 'Patient data is missing.' });
      return;
    }

    setLoading(true);
    try {
      const prospectResponse = await apiClient.post('/mind/prospect', {
        email: patient.email,
        name: patient.name,
        date_0: new Date().toISOString().slice(0, 10),
        consultation_token: patient.consultationToken,
      });

      const remedies = prospectResponse.data;
      if (!remedies || Object.keys(remedies).length === 0) {
        setToast({
          type: 'error',
          message: 'Not enough data found to generate additional questions.',
        });
        setLoading(false);
        return;
      }

      const analyzeResponse = await apiClient.post('/mind/analyze', {
        remedies,
        gender: patient.gender || 'unknown',
        email: patient.email,
        name: patient.name,
        date_0: new Date().toISOString().slice(0, 10),
        consultation_token: patient.consultationToken,
      });

      const valSyms = analyzeResponse?.data?.validated_symptoms || [];
      const weightsMap = analyzeResponse?.data?.symptom_remedy_weights || {};
      const countsMap = analyzeResponse?.data?.symptom_remedy_counts || {};
      const linkedSyms = analyzeResponse?.data?.linked_by_remedy_symptoms || [];
      const countsHighMap = analyzeResponse?.data?.symptom_remedy_high_remedies_counts || {};
      setValidatedSymptoms(valSyms);
      setSymptomRemedyWeights(weightsMap);
      setSymptomRemedyCounts(countsMap);
      setLinkedByRemedySymptoms(linkedSyms);
      setSymptomRemedyHighRemediesCounts(countsHighMap);

      const rawQuestions = (analyzeResponse?.data?.items || []) as MindQuestionItem[];

      // Obtener síntomas ya guardados para pre-seleccionar y priorizar preguntas elegidas
      let existingSyms: string[] = [];
      try {
        const symRes = await apiClient.get('/patients/symptoms', {
          params: {
            email: patient.email,
            date_0: new Date().toISOString().slice(0, 10),
            consultation_token: patient.consultationToken,
          },
        });
        existingSyms = (symRes.data?.symptoms || [])
          .filter((s: { step?: number }) => s.step === 4)
          .map((s: { symptom?: string }) => s.symptom?.trim().toLowerCase() || '');
      } catch (_) {}

      const initialAnswers: Record<string, boolean> = {};
      rawQuestions.forEach((q) => {
        const symClean = q.symptom.trim().toLowerCase();
        if (existingSyms.some((es) => es.includes(symClean) || symClean.includes(es))) {
          initialAnswers[q.symptom] = true;
        }
      });
      setAnswers(initialAnswers);

      const selectedQuestions = rawQuestions.filter((q) => initialAnswers[q.symptom] === true);
      const unselectedQuestions = rawQuestions.filter((q) => !initialAnswers[q.symptom]);

      const initialOrderedUnselected = reorderQuestionsByWeight(
        unselectedQuestions,
        (q) => [q.symptom],
        valSyms,
        Object.keys(initialAnswers),
        weightsMap,
        countsMap,
        linkedSyms,
        { symptomRemedyHighRemediesCounts: countsHighMap }
      );

      const finalOrdered = [...selectedQuestions, ...initialOrderedUnselected];

      setQuestions(finalOrdered);
      setVisitedBlocks(new Set([0]));
      setStep('questions');
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { detail?: string } } };
      const message = errObj?.response?.data?.detail
        ? String(errObj.response.data.detail)
        : 'Error generating mental analysis.';
      setErrorModal({ title: 'Error', message });
    } finally {
      setLoading(false);
    }
  }, [patient, setErrorModal, setToast]);

  const saveMindSymptoms = useCallback(async (symptomsToSave: string[]) => {
    setLoading(true);
    try {
      await apiClient.post('/mind/save', {
        patient: { email: patient.email, name: patient.name, date_0: new Date().toISOString().slice(0, 10), consultation_token: patient.consultationToken },
        symptoms: symptomsToSave,
      });
      await logTemporaryRemedies('Mental Symptoms', 4, patient);
      setToast({ type: 'success', message: 'Mental symptoms updated.' });
      navigate('/intake');
    } catch (_err) {
      setToast({ type: 'error', message: 'Error saving answers.' });
    } finally {
      setLoading(false);
    }
  }, [navigate, patient, setToast]);

  const handleSubmit = useCallback(() => {
    const selectedSymptoms = Object.entries(answers)
      .filter(([_, val]) => val === true)
      .map(([key]) => key);
    saveMindSymptoms(selectedSymptoms);
  }, [answers, saveMindSymptoms]);

  // Habilitar deselección al pulsar sobre el mismo botón elegido
  const handleAnswer = (symptom: string, value: boolean) => {
    setAnswers((prev) => {
      const currentVal = prev[symptom];
      const updated = { ...prev };
      if (currentVal === value) {
        delete updated[symptom];
      } else {
        updated[symptom] = value;
      }

      const newAffirmativeCount = Object.values(updated).filter(Boolean).length;
      if (autoAdvance && newAffirmativeCount >= targetCount && !loading) {
        setTimeout(() => {
          const selected = Object.entries(updated)
            .filter(([_, val]) => val === true)
            .map(([key]) => key);
          saveMindSymptoms(selected);
        }, 100);
      }
      return updated;
    });
  };

  useEffect(() => {
    if (step === 'init' && patient.email) {
      // auto-start si aplica
    }
  }, [patient.email, step]);

  const affirmativeCount = Object.values(answers).filter(Boolean).length;

  const totalBlocks = Math.ceil(questions.length / blockSize);
  const safeBlock = Math.min(currentBlock, Math.max(0, totalBlocks - 1));
  const currentQuestions = questions.slice(safeBlock * blockSize, (safeBlock + 1) * blockSize);

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <ConsultationStepper />

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-secondary text-secondary-content rounded-full p-3">
                <Brain size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Step 4: Mental Symptoms</h2>
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-medium border border-emerald-300">
                    🔒 E2EE Encrypted
                  </span>
                </div>
                <p className="text-sm text-base-content/70">
                  Mark only the questions for which the answer is affirmative in your case. Shared health data is protected via End-to-End Encryption under GDPR & HIPAA standards.
                </p>
              </div>
            </div>
            {step === 'questions' && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => navigate('/intake')}>
                Skip step
              </button>
            )}
          </div>
        </div>
      </div>

      {step === 'init' && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body items-center text-center py-12">
            <button
              type="button"
              className="btn btn-secondary btn-lg"
              onClick={startAnalysis}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Analyzing case...
                </>
              ) : (
                'Start mental analysis'
              )}
            </button>
          </div>
        </div>
      )}

      {step === 'questions' && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {currentQuestions.map((item) => {
              const value = answers[item.symptom];
              return (
                <div key={item.symptom} className={`card bg-base-100 shadow-sm transition-all ${value === true ? 'border-2 border-secondary/40' : ''}`}>
                  <div className="card-body p-4 gap-2">
                    <p className="text-sm font-medium leading-snug min-h-[3.5rem]">
                      {item.question}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className={`btn btn-sm flex-1 ${value === true ? 'btn-success' : 'btn-outline btn-success'}`}
                        onClick={() => handleAnswer(item.symptom, true)}
                      >
                        <Check size={16} /> Yes
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm flex-1 ${value === false ? 'btn-error' : 'btn-outline btn-error'}`}
                        onClick={() => handleAnswer(item.symptom, false)}
                      >
                        <X size={16} /> No
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {questions.length > 0 && (
            <SymptomBlockPager
              totalItems={questions.length}
              blockSize={blockSize}
              currentBlock={safeBlock}
              onBlockChange={handleBlockChange}
              affirmativeCount={affirmativeCount}
              targetCount={targetCount}
              onAdvancePhase={handleSubmit}
              advanceButtonText="Continue to symptom selection"
              isSubmitting={loading}
            />
          )}
        </>
      )}
    </div>
  );
}
